<template>
  <div class="max-w-176" :class="outerClass">
    <div v-if="heading?.value || belowHeading?.value || subheading?.value" class="flex flex-col gap-5 mb-2">
      <div>
        <Text v-if="heading?.value" :field="heading" class="text-xl-bold mb-2" tag="h4" />
        <Text v-if="belowHeading?.value" :field="belowHeading" class="text-base" tag="p" />
      </div>
      <Text v-if="subheading?.value" :field="subheading" class="text-base-bold uppercase" tag="h5" />
    </div>
    <div
      class="border-racq-pale-blue-border flex flex-col basis-full shadow-md bg-racq-true-white"
      :class="{
        'rounded-b-none border-b-0 pt-6 pb-0': showMoreButton,
        'py-6': !showMoreButton,
        'rounded border-2': !isSelfService,
        'rounded-lg border-[1.5px]': isSelfService,
      }"
    >
      <template v-if="title">
        <Text
          v-if="title.value"
          :field="title"
          class="px-8 text-xl-bold text-racq-dark-grey"
          :class="{ 'pb-6': !isSelfService }"
          tag="div"
        />
        <div
          v-else-if="title?.value !== '' && title"
          class="px-8 text-xl-bold text-racq-dark-grey"
          :class="{ 'pb-6': !isSelfService }"
        >
          {{ title }}
        </div>
      </template>

      <div
        v-for="(summary, sumIndex) in reviewSummaries"
        :key="sumIndex"
        class="self-stretch px-5 md:px-8 border-racq-pale-blue-border flex-col justify-start items-start gap-2.5 flex"
        :class="{
          'border-b': (!showMoreButton || expanded) && sumIndex != reviewSummaries.length - 1,
          hidden: !(
            (summary.fields?.items ?? summary.items).length &&
            (!isConfirmationStep || disableExpandToggle || sumIndex == 0 || expanded)
          ),
          'pt-5': sumIndex > 0,
          'pb-5': (!showMoreButton || expanded) && sumIndex != reviewSummaries.length - 1,
        }"
      >
        <div
          :class="{
            'pb-3! gap-2':
              summary.summaryTitle?.value?.length !== 0 ||
              summary.fields?.summaryTitle ||
              summary.fields?.summarySubTitle ||
              summary.summarySubTitle?.value?.length !== 0,
          }"
          class="self-stretch pb-0 flex-col justify-start items-start flex"
        >
          <div class="self-stretch justify-start items-center gap-3 inline-flex">
            <BaseIcon
              v-if="summary.fields?.icon?.value ?? summary.icon?.value"
              :iconClass="(summary.fields?.icon ?? summary.icon)?.value"
            />
            <div class="grow shrink basis-0 text-racq-dark-grey text-lg-bold">
              <Text :field="summary.fields?.summaryTitle ?? summary.summaryTitle" />
            </div>
          </div>
          <template v-if="summary.fields?.summarySubTitle || summary.summarySubTitle">
            <div class="self-stretch justify-start items-start gap-3 inline-flex">
              <div class="grow shrink basis-0 text-racq-dark-grey text-sm">
                <RichText :field="summary.fields?.summarySubTitle ?? summary.summarySubTitle" />
              </div>
            </div>
          </template>
        </div>

        <div
          class="self-stretch flex-col justify-start items-start flex border-racq-pale-blue-border"
          :class="{
            'border-b pb-5': showMoreButton && !expanded,
            'gap-2.5': !isSelfService,
            'gap-3': isSelfService,
          }"
        >
          <ReviewSummaryItem
            v-for="(item, itemIndex) in summary.fields?.items ?? summary.items"
            :key="itemIndex"
            :item="item"
            :isLastReviewSummaryItem="itemIndex === (summary.fields?.items ?? summary.items).length - 1"
            :isSelfService="isSelfService"
          >
            <template #title>
              <div class="relative">
                <RichText :field="item.fields?.title ?? item.title" class="text-racq-dark-grey text-base-bold" />
              </div>
            </template>
            <template #content>
              <RichText
                :field="item.fields?.content ?? item.content"
                class="text-base"
                :class="{
                  'text-block': !isSelfService,
                }"
              />
              <template v-if="shouldRenderMapSlot(item.fields?.content?.value ?? item.content?.value)">
                <slot name="googleMapWithPinStatic"></slot>
              </template>
            </template>
            <template #secondaryLinks>
              <p
                v-if="
                  (item.showFirstLink === undefined || item.showFirstLink === true) &&
                  item.fields?.firstLink?.value.text
                "
                class="self-stretch mb-2 flex flex-col md:block gap-4"
              >
                <Link
                  v-if="item.fields.firstLink?.value?.href"
                  :field="item.fields.firstLink"
                  class="text-m-bold mr-4"
                />
                <Link v-if="item.fields.secondLink?.value?.href" :field="item.fields.secondLink" class="text-m-bold" />
              </p>
            </template>
            <template v-if="item.name !== ''" #subContent>
              <slot :name="`subContent-${item.name}`"></slot>
              <div
                v-if="
                  (item.showSubcontent === undefined || item.showSubcontent === true) && item.fields?.subcontent?.value
                "
                class="self-stretch text-paragraph-color text-base break-words"
              >
                <!-- RichText unable to forward querystring -->
                <div v-html="item.fields.subcontent.value"></div>
              </div>
            </template>
            <template #edit>
              <Link
                v-if="
                  (item.showPrimaryLink === undefined || item.showPrimaryLink === true) &&
                  item.fields?.primaryLink?.value?.href
                "
                :field="item.fields.primaryLink"
                class="link text-base-bold"
              />
              <Link
                v-if="item.editUrl && !isConfirmationStep && item.showEdit"
                :field="formatLink(item.editUrl)"
                class="text-base-bold underline"
                >Edit</Link
              >
              <button
                v-if="item.editGoToStep != null && !isConfirmationStep && item.showEdit"
                type="button"
                class="link text-base-bold"
                @click="onEditDetails(item.editGoToStep, summary, item)"
              >
                Edit
              </button>
            </template>
          </ReviewSummaryItem>
          <slot
            v-if="
              !summaryItemFooterOnLastSummaryOnly ||
              (summaryItemFooterOnLastSummaryOnly && sumIndex === reviewSummaries.length - 1)
            "
            name="summaryItemFooter"
          ></slot>
        </div>
      </div>

      <div v-if="showDiscounts || !isConfirmationStep || expanded">
        <slot name="additionalPeople"></slot>
        <slot name="policyDiscounts"></slot>
        <slot name="ssPolicyDiscounts"></slot>
      </div>
    </div>
    <div
      v-if="showMoreButton"
      :class="{
        'border-t-0': !expanded,
        'rounded border-2': !isSelfService,
        'rounded-lg border-[1.5px]': isSelfService,
      }"
      class="border-racq-pale-blue-border flex flex-col basis-full border-t-0 rounded-t-none bg-white"
    >
      <div class="w-100 flex items-center justify-center mb-5 gap-4">
        <ShowHideButton :showMore="expanded" @click="expanded = !expanded" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { IReviewSummary, IReviewSummaryItem } from '@/interfaces';
import ReviewSummaryItem from '@/components/ReviewSummary/ReviewSummaryItem.vue';
import BaseIcon from '@/components/BaseIcon/BaseIcon.vue';
import ShowHideButton from '@/components/ShowHideButton/ShowHideButton.vue';
import { useFormStore } from '@/store';
import { Text, RichText, Link, Field } from '@sitecore-jss/sitecore-jss-vue';
import { LinkField } from '@sitecore-jss/sitecore-jss-vue/types/components/Link';

const props = defineProps<{
  reviewSummaries: IReviewSummary[];
  isConfirmationStep: boolean;
  disableExpandToggle?: boolean;
  showDiscounts?: boolean;
  title?: string | Field<string>;
  summaryItemFooterOnLastSummaryOnly?: boolean;
  startExpanded?: boolean;
  isSelfService?: boolean;
  heading?: Field<string>;
  belowHeading?: Field<string>;
  subheading?: Field<string>;
  outerClass?: string;
}>();
const formStore = useFormStore();
const expanded = ref<boolean>(props.startExpanded ?? false);
const showMoreButton = computed(
  () => props.isConfirmationStep && !props.disableExpandToggle && props.reviewSummaries?.length > 1
);

// Exposed to track which "edit" link was clicked from the ReviewSummary
const emit = defineEmits<(e: 'editClicked', summary: IReviewSummary, item: IReviewSummaryItem) => void>();
function onEditDetails(goToStep: number, reviewSummary: IReviewSummary, reviewSummaryItem: IReviewSummaryItem) {
  formStore.clearLastStepSubmitted();
  emit('editClicked', reviewSummary, reviewSummaryItem);
  formStore.goToStep(goToStep);
}

/**
 * Returns whether the reviewSummaryItemContent contains a slot item
 * @param reviewSummaryItemContent The ReviewSummaryItemContent to check.
 */
function shouldRenderMapSlot(reviewSummaryItemContent: string): boolean {
  if (reviewSummaryItemContent?.includes('#googleMapWithPinStatic')) {
    return true;
  }
  return false;
}

function formatLink(url: string): LinkField {
  formStore.clearLastStepSubmitted();
  return {
    value: {
      href: url,
      target: '_self',
    },
  };
}
</script>
